import { yawObject } from './scene.js';
import { playHurt, playStep } from './audio.js';
import { BTN } from './net.js';
import {
  player, S, clock, touchState,
  R, STEP_TOL, STAND, CROUCH, PRONE, LEAN_OFFSET,
} from './state.js';
import { nearPoint, nearRay } from './mapgrid.js';
import { spawnPoint } from './world.js';

// ============================================================
//  Игрок: движение, коллизии, опора, урон/смерть/респавн.
//
//  ⚠️ ГЛАВНОЕ ПРО stepPhysics: это построчный порт shared/src/movement.rs.
//  Формулы и ПОРЯДОК операций обязаны совпадать с сервером — иначе клиент
//  предскажет одно, сервер посчитает другое, и reconciliation будет вечно
//  дёргать игрока назад. Любая правка движения делается СРАЗУ в двух местах.
//
//  Функция чистая относительно внешнего состояния: работает над переданным
//  `st`, ничего не знает про камеру, звук и ввод. Это нужно, чтобы её можно
//  было прогонять десятки раз за кадр при переигрывании (prediction.js).
// ============================================================

/** Выталкивание из ящиков и других игроков. `st` — {pos, vel, eye}. */
export function resolveCollisions(st, others) {
  const r = player.radius, p = st.pos;
  pushOutWalls(st, r);
  // упор в сетевых игроков (круг-круг в плоскости XZ)
  //
  // Локальных ботов здесь СПЕЦИАЛЬНО нет: сервер про них не знает, и упор
  // в бота давал бы расхождение, которое reconciliation честно откатывал бы
  // каждый тик — игрока бы трясло у бота. Боты остались мишенями для стрельбы
  // и уйдут, когда их заменят сетевые игроки.
  let shoved = false;
  for (const o of others) {
    const dx = p.x - o.x, dz = p.z - o.z, rr = r + 0.45, d2 = dx * dx + dz * dz;
    if (d2 < rr * rr && d2 > 1e-6) {
      const d = Math.sqrt(d2), push = rr - d;
      p.x += dx / d * push; p.z += dz / d * push; shoved = true;
    }
  }
  // ⚠️ ВТОРОЙ проход по стенам — зеркало movement.rs::resolve_collisions.
  // Расталкивание людей двигает на величину до r + 0.45 = 0.8 м и про
  // геометрию не знает вовсе: прижатого к стене толкало ВНУТРЬ стены.
  // Гоняем только когда толчок реально был.
  if (shoved) pushOutWalls(st, r);
  const lim = R - 1; p.x = Math.max(-lim, Math.min(lim, p.x)); p.z = Math.max(-lim, Math.min(lim, p.z));
}

/** Выталкивание из боксов карты — порт movement.rs::push_out_walls. */
function pushOutWalls(st, r) {
  const p = st.pos;
  // ⚠️ Кандидатов берём из сетки (mapgrid.js), а не перебором всех 1743 боксов:
  // формула ниже та же, что была и что стоит в movement.rs, — сузился только
  // список тех, до кого вообще можно дотянуться. Почему это стало нужно —
  // в шапке mapgrid.js.
  //
  // ⚠️ Радиус запроса с запасом (r + 0.5), а не ровно r: выталкивание идёт
  // накопительно, и следующий бокс проверяется уже по сдвинутой позиции.
  // Возьмём впритык — угол между двумя ящиками сможет протолкнуть игрока
  // мимо второго, наружу геометрии. Запас в полметра ничего не стоит:
  // клетка сетки 4 м, обычно это ровно те же клетки.
  nearPoint(p.x, p.z, r + 0.5, (c) => {
    const feet = p.y - st.eye;
    // не толкать, если игрок стоит на верху ящика (в пределах допуска) или полностью ниже него
    if (feet > c.max.y - STEP_TOL || feet + st.eye < c.min.y) return;
    // ---- центр УЖЕ внутри бокса ----
    // Порт movement.rs::push_out_aabb, там же и разбор бага: ближайшая точка
    // бокса совпадает с центром игрока, dx = dz = 0, и обычная формула ниже
    // молча не толкает — тело остаётся в геометрии навсегда (симптом:
    // «ходит сквозь коробки», «исчез в стене»). Выходим по ближайшей грани.
    // Условие `c.min.y <= feet` отсекает перекладину над головой: из проёма
    // с низкой притолокой выкидывать вбок нельзя (разбор — в movement.rs).
    if (c.min.y <= feet + 0.05 && p.x > c.min.x && p.x < c.max.x && p.z > c.min.z && p.z < c.max.z) {
      const west = p.x - c.min.x, east = c.max.x - p.x;
      const north = p.z - c.min.z, south = c.max.z - p.z;
      const near = Math.min(west, east, north, south);
      // Порядок сравнений — часть зеркала: на равных расстояниях обе стороны
      // обязаны выбрать одну и ту же грань.
      if (near === west) p.x = c.min.x - r;
      else if (near === east) p.x = c.max.x + r;
      else if (near === north) p.z = c.min.z - r;
      else p.z = c.max.z + r;
      return;
    }
    const cx = Math.max(c.min.x, Math.min(p.x, c.max.x)), cz = Math.max(c.min.z, Math.min(p.z, c.max.z));
    const dx = p.x - cx, dz = p.z - cz, d2 = dx * dx + dz * dz;
    if (d2 < r * r && d2 > 1e-6) { const d = Math.sqrt(d2), push = r - d; p.x += dx / d * push; p.z += dz / d * push; }
  });
}

// высота опоры под игроком: пол (0) или верх ящика, на котором он стоит/над которым падает
export function supportHeight(p, r, eye = player.eye) {
  let g = 0; const feet = p.y - eye;
  nearPoint(p.x, p.z, r, (c) => {
    if (p.x > c.min.x - r && p.x < c.max.x + r && p.z > c.min.z - r && p.z < c.max.z + r) {
      if (c.max.y > g && feet >= c.max.y - STEP_TOL) g = c.max.y;
    }
  });
  return g;
}

// ⚠️ Зеркало movement.rs::PRONE_LEN / PRONE_LEN_EXIT — менять только парой.
// Длина тела лёжа (глаз → ступни вдоль локальной +Z) и гистерезис выхода.
const PRONE_LEN = 1.74, PRONE_LEN_EXIT = 1.4;

// ---- Выносливость (⚠️ зеркало movement.rs::STAMINA_* — менять только парой) ----
//
// Стамина считается ЗДЕСЬ, внутри предсказания, а не отдельным таймером в HUD:
// от неё зависит скорость, то есть позиция, а всё, что влияет на позицию,
// обязано считаться одинаково у клиента и сервера. Правду сервер присылает
// в кадре OP_SELF (см. prediction.js), клиент до тех пор считает сам.
export const STAMINA_MAX = 1;
const STAMINA_DRAIN = 1 / 5;         // полный запас = 5 секунд бега
const STAMINA_REGEN = 1 / 20;        // с нуля до полной за 20 секунд шагом
// Сколько надо накопить, чтобы снова побежать. Задаёт РИТМ бега с зажатым
// Shift после истощения: 0.25 = 5 с передышки на 1.25 с бега.
export const STAMINA_SPRINT_MIN = 0.25;

// ---- Под огнём (⚠️ зеркало movement.rs::HURT_SLOW / HURT_SPEED_MUL) ----
//
// Попадание сбивает шаг: пока в игрока стреляют, бежать он не может, а идёт
// тяжело. Живёт здесь, внутри предсказания, по той же причине, что стамина —
// от этого зависит скорость, то есть позиция. Правду присылает сервер
// в OP_SELF (протокол 19), клиент до тех пор считает сам.
export const HURT_SLOW = 0.6;        // с — сколько держится замедление
const HURT_SPEED_MUL = 0.55;         // во сколько раз медленнее шаг раненого

/** Хватает ли сил бежать. Гистерезис: бегущему хватает остатка, начать бег
 *  заново можно только с STAMINA_SPRINT_MIN — иначе на нуле поза оружия
 *  и звук шагов дребезжали бы каждый кадр. Порт movement.rs::sprint_allowed. */
function sprintAllowed(st) {
  return st.sprinting ? st.stamina > 0 : st.stamina >= STAMINA_SPRINT_MIN;
}

/**
 * Есть ли позади место, чтобы лечь — порт movement.rs::prone_clear.
 * Гистерезис по текущей высоте глаз: уже лежащему нужно меньше места.
 */
function proneClear(st, yaw) {
  const already = st.eye < (PRONE + CROUCH) * 0.5;
  const need = already ? PRONE_LEN_EXIT : PRONE_LEN;
  // локальная +Z (назад) в мире — (sin yaw, 0, cos yaw)
  return rayWalls(st.pos.x, st.pos.y, st.pos.z, Math.sin(yaw), 0, Math.cos(yaw), PRONE_LEN) >= need;
}

/**
 * Один шаг физики — ровно то же, что делает сервер в movement.rs::step.
 * `st`: {pos: Vector3, vel: Vector3, eye: number, onGround: bool}
 */
export function stepPhysics(st, buttons, yaw, dt, others) {
  // Лечь можно только там, где сзади есть место под тело (иначе корпус уезжает
  // в стену). Проверяется каждый тик — поворот телом в стену снимает позу.
  const proneWanted = (buttons & BTN.PRONE) !== 0;
  const prone = proneWanted && proneClear(st, yaw);
  // Не удалось лечь — приседаем, а не встаём в полный рост.
  const crouch = ((buttons & BTN.CROUCH) !== 0 || (proneWanted && !prone)) && !prone;
  // Намерение бежать (Shift вне приседа/лёжа). Факт считаем ниже — нужна ещё
  // выносливость и реальное движение. ⚠️ Порт movement.rs::sprint_intent.
  const sprintWanted = (buttons & BTN.SPRINT) !== 0 && !crouch && !prone;

  const eyeTarget = prone ? PRONE : crouch ? CROUCH : STAND;
  st.eye += (eyeTarget - st.eye) * Math.min(dt * 10, 1);

  // ⚠️ Направление считается ДО выбора скорости (сервер переставил так же):
  // бег требует, чтобы игрок реально куда-то шёл, иначе «Shift на месте»
  // сжигал бы силы стоя. От скорости wx/wz/moving не зависят.
  const sin = Math.sin(yaw), cos = Math.cos(yaw);
  let wx = 0, wz = 0;
  if (buttons & BTN.W) { wx -= sin; wz -= cos; }
  if (buttons & BTN.S) { wx += sin; wz += cos; }
  if (buttons & BTN.A) { wx -= cos; wz += sin; }
  if (buttons & BTN.D) { wx += cos; wz -= sin; }

  // sqrt, а не Math.hypot: hypot реализован по-разному в разных рантаймах
  const wl = Math.sqrt(wx * wx + wz * wz);
  const moving = wl > 0.05;
  if (moving) { wx /= wl; wz /= wl; }

  // ---- Под огнём (порт movement.rs) ----
  // Окно «в меня прямо сейчас попадают» тикает ЗДЕСЬ, внутри симуляции, а не
  // отдельным таймером: сервер считает его теми же кадрами ввода, и разъедься
  // они — замедление кончалось бы у нас раньше или позже, чем у него.
  st.hurt = Math.max(0, (st.hurt || 0) - dt);
  const hurt = st.hurt > 0;

  // ---- Выносливость (порт movement.rs) ----
  // Бежит игрок или нет — решается здесь и только здесь; результат живёт
  // в st.sprinting, и по нему потом прижимается оружие, звучит шаг и
  // отменяется перезарядка. Выдохся — идёшь шагом, ничего не отпуская.
  // ⚠️ Попадание запрещает бег ровно тем же путём, что истощение: через факт,
  // а не через кнопки, — иначе подстреленный звучал бы бегущим и держал бы
  // оружие прижатым, не имея права стрелять.
  const sprint = sprintWanted && moving && sprintAllowed(st) && !hurt;
  st.sprinting = sprint;
  st.stamina = sprint
    ? Math.max(0, st.stamina - STAMINA_DRAIN * dt)
    : Math.min(STAMINA_MAX, st.stamina + STAMINA_REGEN * dt);

  // Множитель идёт ПОВЕРХ позы — под огнём тяжело и идти, и ползти. Он же
  // попадает в `k` ниже: раненый ещё и трогается вязче.
  const speed = (prone ? 1.6 : crouch ? 3.2 : sprint ? 9 : 6) * (hurt ? HURT_SPEED_MUL : 1);
  const accel = st.onGround ? 60 : 12;

  const wishSpeed = moving ? speed : 0;
  const k = Math.min(accel * dt / speed, 1);
  st.vel.x += (wx * wishSpeed - st.vel.x) * k;
  st.vel.z += (wz * wishSpeed - st.vel.z) * k;

  const groundY = supportHeight(st.pos, player.radius, st.eye);
  const eyeLevel = groundY + st.eye;

  if ((buttons & BTN.JUMP) && st.onGround && !crouch && !prone) { st.vel.y = 7; st.onGround = false; }
  st.vel.y -= 20 * dt;
  st.pos.x += st.vel.x * dt; st.pos.z += st.vel.z * dt; st.pos.y += st.vel.y * dt;

  const landed = st.pos.y <= eyeLevel && !st.onGround;
  if (st.pos.y <= eyeLevel) {
    st.pos.y = eyeLevel; st.vel.y = 0; st.onGround = true;
  } else st.onGround = false;
  if (st.onGround) st.pos.y = eyeLevel;

  resolveCollisions(st, others);
  // ⚠️ `sprint` здесь — ФАКТ бега (с учётом выносливости), а не «зажат Shift»:
  // на него смотрят поза оружия, звук шага, бит HELD_SPRINT и отмена
  // перезарядки, и все они обязаны видеть то же, что посчитал сервер.
  return { moving, sprint, crouch, prone, landed };
}

/**
 * Локальный урон — от ботов и собственных гранат.
 *
 * ⚠️ В онлайне НЕ снимает HP: здоровье считает сервер, и списание здесь
 * всё равно было бы затёрто следующим кадром OP_SELF — то есть выглядело бы
 * как «урон прошёл и сам собой отменился». Боты и гранаты в сеть пока не
 * заведены (сервер про них не знает), поэтому их урон остаётся визуальным.
 * Когда гранаты появятся на сервере, они пойдут обычным путём OP_DAMAGE.
 */
/**
 * @param {number} dmg урон
 * @param {boolean} [slow] сбивает ли этот урон шаг (`HURT_SLOW`). Пуля и осколок —
 *   да, тик огня — НЕТ: он идёт каждые 0.4 с, и горящий оказался бы замедлен все
 *   пять секунд догорания. Зеркало `room.rs`: там окно ставят только выстрел
 *   и осколок, а `step_fires` — нет.
 */
export function hurtPlayer(dmg, slow = true) {
  // Замедление от попадания в онлайне приезжает готовым в OP_SELF (сервер
  // считает окно сам). Ставить его ещё и здесь нельзя: локальный урон бывает
  // и от того, чего на сервере нет вовсе, — и клиент замедлился бы там, где
  // сервер этого не делал, то есть заработал бы откат позиции каждый тик.
  if (S.netState !== 'online') {
    player.hp -= dmg;
    if (slow) player.hurt = HURT_SLOW;
  }
  player.lastHurt = clock.elapsedTime;
  S.shake = Math.min(S.shake + 0.05, 0.1);
  const hf = document.getElementById('hitflash'); hf.style.opacity = '0.5';
  setTimeout(() => hf.style.opacity = '0', 80);
  playHurt();
  if (player.hp <= 0 && player.alive) killPlayer();
}

export function killPlayer() {
  player.alive = false; player.hp = 0; player.deadUntil = clock.elapsedTime + 2.5;
  // Сброс гранаты «в руке» делает игровой цикл (main.js) — здесь импорт grenades.js
  // создал бы цикл player ↔ grenades.
  document.getElementById('dead').classList.add('on');
}

export function respawnPlayer() {
  player.alive = true; player.hp = 100;
  // ⚠️ Точка — со спавнов карты, а не зашитая числом. Раньше здесь стояло
  // `(0, STAND, 22)` из рукописной арены: на de_dust2 это чужое место и
  // высота «пол на нуле», то есть воскресший на четверть секунды (пока не
  // придёт серверный OP_SELF) оказывался внутри геометрии. В офлайне, где
  // сервера нет вовсе, он там и оставался. Авторитет всё равно за сервером —
  // см. spawnPoint() в world.js и Phys::spawn_on на сервере.
  const sp = spawnPoint();
  player.pos.set(sp.x, sp.y, sp.z);
  player.vel.set(0, 0, 0); player.eye = STAND;
  // Дыхание тоже возвращается полностью — зеркало серверного `Phys::spawn_on`
  // (воскреснуть выдохшимся значило бы наказать за смерть дважды).
  player.stamina = STAMINA_MAX; player.sprinting = false;
  // И «сбит с шага» смерть тоже смывает — воскреснуть замедленным той самой
  // очередью, которая тебя убила, значило бы наказать дважды. Ровно там же
  // это делает сервер (`Phys::spawn_on`).
  player.hurt = 0;
  S.yaw = sp.yaw; S.pitch = 0;
  // Поза не переживает смерть: возрождаемся СТОЯ, а не сидя/лёжа. Тумблеры
  // приседа/лёжа клиентские (input.js) и снимаются здесь же — иначе клиент
  // продолжил бы слать серверу BTN_CROUCH/PRONE, и игрок вставал бы из
  // мёртвых уже пригнувшимся (жалоба владельца). Тач-кнопки — тот же тумблер;
  // их подсветка (класс .on) сама поправится при следующем тапе.
  S.crouchToggle = false; S.proneToggle = false; touchState.crouch = false;
  document.getElementById('btnCrouch')?.classList.remove('on');
  document.getElementById('btnProne')?.classList.remove('on');
  // Смерть смывает ослепление. Это не косметика: сервер снимает своё окно
  // «ничего не вижу» ровно здесь же (room.rs, ветка респавна), и оставить
  // белый экран после воскрешения значило бы развести их по времени.
  S.flashAmt = 0; S.flashHold = 0;
  // И горение: смерть тушит. Ровно там же его гасит сервер (`burn_left = 0`
  // в ветке респавна room.rs) — воскреснуть подожжённым и сгореть второй раз
  // от лужи, которой рядом нет, это не наказание, а баг.
  S.burning = false; S.burnLeft = 0; S.burnNext = 0;
  document.getElementById('flash').style.opacity = 0;
  document.getElementById('dead').classList.remove('on');
}

// ---- Наклон Q/E ----
//
// ⚠️ Порт `shared/src/movement.rs::lean_shift` — менять только вместе с ним.
// Сервер считает видимость и выстрел из НАКЛОНЁННОЙ точки глаз, поэтому
// камера обязана стоять там же. Разъедутся — игрок будет целиться из одной
// точки, а попадать из другой.

const LEAN_MARGIN = 0.12;

/**
 * Пересечение луча с AABB — порт `hit.rs::ray_aabb`. Возвращает вход или −1.
 *
 * ⚠️ Осей три, но развёрнуты они вручную, а не циклом по массивам, как было
 * раньше. Прежняя версия заводила по два массива на КАЖДЫЙ проверяемый бокс;
 * на блокауте из 57 коробок это был мусор в пределах шума, на 1743 — под
 * миллион временных массивов в секунду (луч гоняется каждый шаг физики, и
 * ещё раз на каждый переигранный ввод при reconciliation). Ровно тот случай,
 * когда красивее — значит медленнее на порядок.
 *
 * −1 вместо null: null заставлял бы вызывающего проверять тип на каждом боксе.
 */
function rayAabb(ox, oy, oz, dx, dy, dz, c) {
  let tmin = 0, tmax = Infinity;
  if (Math.abs(dx) < 1e-8) { if (ox < c.min.x || ox > c.max.x) return -1; }
  else {
    const inv = 1 / dx;
    let t1 = (c.min.x - ox) * inv, t2 = (c.max.x - ox) * inv;
    if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1; if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return -1;
  }
  if (Math.abs(dy) < 1e-8) { if (oy < c.min.y || oy > c.max.y) return -1; }
  else {
    const inv = 1 / dy;
    let t1 = (c.min.y - oy) * inv, t2 = (c.max.y - oy) * inv;
    if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1; if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return -1;
  }
  if (Math.abs(dz) < 1e-8) { if (oz < c.min.z || oz > c.max.z) return -1; }
  else {
    const inv = 1 / dz;
    let t1 = (c.min.z - oz) * inv, t2 = (c.max.z - oz) * inv;
    if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1; if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return -1;
  }
  return tmin;
}

/**
 * Ближайшая стена по лучу — порт `hit.rs::ray_walls`.
 *
 * Кандидаты берутся сеткой (mapgrid.js) вдоль самого луча, а не перебором
 * всей карты. Ответ тот же: сетка отбрасывает только те боксы, мимо которых
 * луч и так не проходит.
 */
export function rayWalls(ox, oy, oz, dx, dy, dz, max) {
  let best = max;
  nearRay(ox, oz, dx, dz, max, (c) => {
    const t = rayAabb(ox, oy, oz, dx, dy, dz, c);
    if (t >= 0 && t < best) best = t;
  });
  return best;
}

/**
 * Фактическое боковое смещение при наклоне — с упором в геометрию.
 * `lean` — намерение −1…1, возвращает метры вдоль локальной оси X (вправо).
 */
export function leanShift(pos, yaw, lean) {
  const want = Math.max(-1, Math.min(1, lean)) * LEAN_OFFSET;
  if (Math.abs(want) < 1e-4) return 0;
  const sign = want > 0 ? 1 : -1;
  // локальная ось X игрока в мире — «вправо»
  const dx = Math.cos(yaw) * sign, dz = -Math.sin(yaw) * sign;
  const reach = Math.abs(want) + LEAN_MARGIN;
  const wall = rayWalls(pos.x, pos.y, pos.z, dx, 0, dz, reach);
  return Math.max(0, Math.min(wall - LEAN_MARGIN, Math.abs(want))) * sign;
}

/**
 * Эффекты движения, которые НЕ должны переигрываться при reconciliation:
 * звук шагов и тряска при приземлении. Если бы они жили внутри stepPhysics,
 * каждое переигрывание истории лупило бы шагами по ушам.
 */
export function movementEffects(mv, dt) {
  if (mv.landed) S.shake = Math.min(S.shake + 0.04, 0.08);
  if (mv.moving && player.onGround) {
    S.stepDist += Math.sqrt(player.vel.x * player.vel.x + player.vel.z * player.vel.z) * dt;
    // mv.sprint — уже факт бега (включает и «идёт», и «хватило сил»),
    // поэтому выдохшийся звучит шагом, как и слышит его сервер.
    if (S.stepDist > 2.2) { S.stepDist = 0; playStep(mv.sprint); }
  }
  yawObject.position.copy(player.pos);
}
